package de.xtl.retroguide.join

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.FilterChip
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewmodel.compose.viewModel
import de.xtl.retroguide.AppTheme
import de.xtl.retroguide.CelebrationConfetti
import de.xtl.retroguide.SessionAccessStorage
import de.xtl.retroguide.lobby.ModeratorLobbyScreen
import de.xtl.retroguide.lobby.ParticipantLobbyScreen
import de.xtl.retroguide.question.ModeratorQuestionScreen
import de.xtl.retroguide.question.ModeratorSummaryScreen
import de.xtl.retroguide.question.ParticipantQuestionScreen
import org.jetbrains.compose.resources.stringResource
import retroguide.app.shared.generated.resources.Res
import retroguide.app.shared.generated.resources.create_retro_action
import retroguide.app.shared.generated.resources.display_name_label
import retroguide.app.shared.generated.resources.display_name_placeholder
import retroguide.app.shared.generated.resources.error_connection
import retroguide.app.shared.generated.resources.error_action_rejected
import retroguide.app.shared.generated.resources.error_name_required
import retroguide.app.shared.generated.resources.error_session_not_found
import retroguide.app.shared.generated.resources.error_name_unavailable
import retroguide.app.shared.generated.resources.error_rate_limited
import retroguide.app.shared.generated.resources.join_action
import retroguide.app.shared.generated.resources.join_hint
import retroguide.app.shared.generated.resources.join_loading
import retroguide.app.shared.generated.resources.join_subtitle
import retroguide.app.shared.generated.resources.join_title
import retroguide.app.shared.generated.resources.session_code_label
import retroguide.app.shared.generated.resources.session_code_placeholder
import retroguide.app.shared.generated.resources.name_selection_heading
import retroguide.app.shared.generated.resources.name_option_occupied
import retroguide.app.shared.generated.resources.name_options_loading

@Composable
fun JoinSessionRoot(
    sessionAccessStorage: SessionAccessStorage,
    initialSessionCode: String? = null,
    onDownloadMarkdown: (String, String) -> Unit,
    onShareMarkdown: (String, String) -> Unit,
    viewModel: JoinSessionViewModel = viewModel {
        JoinSessionViewModel(
            initialSessionCode = initialSessionCode,
            sessionAccessStorage = sessionAccessStorage,
        )
    },
) {
    LaunchedEffect(initialSessionCode) {
        initialSessionCode?.let { viewModel.onAction(JoinSessionAction.OnSessionCodeChange(it)) }
    }
    LaunchedEffect(viewModel) {
        viewModel.events.collect { event ->
            when (event) {
                is JoinSessionEvent.DownloadMarkdown -> onDownloadMarkdown(event.fileName, event.content)
                is JoinSessionEvent.ShareMarkdown -> onShareMarkdown(event.fileName, event.content)
            }
        }
    }

    val state by viewModel.state.collectAsStateWithLifecycle()
    Box(Modifier.fillMaxSize()) {
        when (state.screen) {
            RetroScreen.JOIN -> JoinSessionScreen(state = state, onAction = viewModel::onAction)
            RetroScreen.MODERATOR_LOBBY -> ModeratorLobbyScreen(
                state = state,
                onAction = viewModel::onAction,
            )

            RetroScreen.PARTICIPANT_LOBBY -> ParticipantLobbyScreen(
                state = state,
                onAction = viewModel::onAction,
            )

            RetroScreen.MODERATOR_QUESTION -> ModeratorQuestionScreen(state, viewModel::onAction)
            RetroScreen.PARTICIPANT_QUESTION -> ParticipantQuestionScreen(state, viewModel::onAction)
            RetroScreen.MODERATOR_SUMMARY -> ModeratorSummaryScreen(state, viewModel::onAction)
        }
        if (state.showSessionEndedDialog) CelebrationConfetti(Modifier.matchParentSize())
    }
}

@Composable
fun JoinSessionScreen(
    state: JoinSessionState,
    onAction: (JoinSessionAction) -> Unit,
) {
    Surface(Modifier.fillMaxSize()) {
        Box(
            modifier = Modifier.fillMaxSize().safeDrawingPadding().verticalScroll(rememberScrollState()),
            contentAlignment = Alignment.TopCenter,
        ) {
            Column(
                modifier = Modifier
                    .widthIn(max = 412.dp)
                    .fillMaxWidth()
                    .padding(horizontal = 24.dp, vertical = 48.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp),
            ) {
                Text(
                    text = stringResource(Res.string.join_title),
                    style = MaterialTheme.typography.titleLarge,
                )
                Text(
                    text = stringResource(Res.string.join_subtitle),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Spacer(Modifier.height(8.dp))
                OutlinedTextField(
                    value = state.sessionCode,
                    onValueChange = { onAction(JoinSessionAction.OnSessionCodeChange(it)) },
                    label = { Text(stringResource(Res.string.session_code_label)) },
                    placeholder = { Text(stringResource(Res.string.session_code_placeholder)) },
                    singleLine = true,
                    enabled = !state.isLoading,
                    keyboardOptions = KeyboardOptions(
                        capitalization = KeyboardCapitalization.Characters,
                        imeAction = ImeAction.Done,
                    ),
                    modifier = Modifier.fillMaxWidth(),
                )
                if (state.sessionCode.length == de.xtl.retroguide.protocol.SESSION_CODE_LENGTH) {
                    Text(
                        text = stringResource(Res.string.name_selection_heading),
                        style = MaterialTheme.typography.titleMedium,
                    )
                    if (state.isRosterLoading) {
                        Text(
                            text = stringResource(Res.string.name_options_loading),
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    } else if (state.participantNameOptions.isNotEmpty()) {
                        FlowRow(
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            verticalArrangement = Arrangement.spacedBy(8.dp),
                        ) {
                            state.participantNameOptions.forEach { option ->
                                FilterChip(
                                    selected = state.selectedParticipantName == option.name,
                                    onClick = {
                                        onAction(JoinSessionAction.OnParticipantNameSelect(option.name))
                                    },
                                    enabled = !state.isLoading && !option.isSelected,
                                    label = {
                                        Text(
                                            if (option.isSelected) {
                                                stringResource(Res.string.name_option_occupied, option.name)
                                            } else {
                                                option.name
                                            },
                                        )
                                    },
                                )
                            }
                        }
                    }
                }
                Spacer(Modifier.height(8.dp))
                Button(
                    onClick = { onAction(JoinSessionAction.OnJoinClick) },
                    enabled = state.canJoin,
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Text(stringResource(Res.string.join_action))
                }
                OutlinedTextField(
                    value = state.displayName,
                    onValueChange = { onAction(JoinSessionAction.OnDisplayNameChange(it)) },
                    label = { Text(stringResource(Res.string.display_name_label)) },
                    placeholder = { Text(stringResource(Res.string.display_name_placeholder)) },
                    singleLine = true,
                    enabled = !state.isLoading,
                    modifier = Modifier.fillMaxWidth(),
                )
                TextButton(
                    onClick = { onAction(JoinSessionAction.OnCreateRetroClick) },
                    enabled = !state.isLoading,
                    modifier = Modifier.align(Alignment.CenterHorizontally),
                ) {
                    Text(stringResource(Res.string.create_retro_action))
                }
                val message = when {
                    state.isLoading -> Res.string.join_loading
                    state.error == JoinSessionError.NAME_REQUIRED -> Res.string.error_name_required
                    state.error == JoinSessionError.SESSION_NOT_FOUND -> Res.string.error_session_not_found
                    state.error == JoinSessionError.CONNECTION -> Res.string.error_connection
                    state.error == JoinSessionError.ACTION_REJECTED -> Res.string.error_action_rejected
                    state.error == JoinSessionError.NAME_UNAVAILABLE -> Res.string.error_name_unavailable
                    state.error == JoinSessionError.RATE_LIMITED -> Res.string.error_rate_limited
                    else -> Res.string.join_hint
                }
                Text(
                    text = stringResource(message),
                    style = MaterialTheme.typography.bodySmall,
                    color = if (state.error == null) {
                        MaterialTheme.colorScheme.onSurfaceVariant
                    } else {
                        MaterialTheme.colorScheme.error
                    },
                    modifier = Modifier.align(Alignment.CenterHorizontally),
                )
            }
        }
    }
}

@Preview
@Composable
private fun JoinSessionInitialScreenPreview() {
    AppTheme {
        JoinSessionScreen(
            state = JoinSessionState(),
            onAction = {},
        )
    }
}
